import { shorthands, makeStyles, mergeClasses } from "@griffel/react";
import { HighContrastSelector } from "@fluentui/style-utilities";

// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastBackgroundHoverFocus,
  commonHighContrastColorHoverFocus,
  commonHighContrastOutlineToggledOn,
} from "@1js/acui-common/lib/styling/next/commonStyles";
import {
  useCommonRibbonButtonStyles,
  getRibbonButtonTypeStyles,
} from "../../next/commonRibbonButtonStyles";
import { RibbonButtonState } from "../../RibbonButton/next";
import { RibbonMenuButtonState } from ".";
import { menuTextContainerClass } from "@1js/acui-button";
import { textContainerClassName } from "@1js/acui-common";
import { customTokens } from "@1js/acui-theme";

const useBaseStyles = makeStyles({
  medium: {
    paddingLeft: "9px",
    paddingRight: "0px",
    [`& .${textContainerClassName}`]: {
      paddingLeft: "10px",
    },
  },
  small: {
    paddingTop: "1px",
    paddingLeft: "0px",
    paddingRight: "0px",
    paddingBottom: "1px",
    [`& .${textContainerClassName}`]: {
      paddingLeft: "4px",
    },
  },
  large: {
    paddingLeft: "0px",
    paddingTop: "1px",
    paddingRight: "0px",
    paddingBottom: "1px",
    [`& .${menuTextContainerClass}`]: {
      whiteSpace: "nowrap",
    },
  },
  expanded: {
    backgroundColor: customTokens.legacy_neutralLight,
    ...shorthands.borderColor("transparent"),
    color: customTokens.legacy_neutralDark,

    [HighContrastSelector]: {
      ...commonHighContrastBackgroundHoverFocus,
      ...commonHighContrastColorHoverFocus,
      ...commonHighContrastOutlineToggledOn,
    },
  },
  overflowButton: {
    paddingRight: "9px",
  },
});

const useIconStyles = makeStyles({
  small: {
    marginLeft: "1px",
  },
  medium: {
    marginRight: "0px",
    paddingLeft: "0px",
    marginTop: "2px",
  },
  large: {
    marginTop: "1px",
  },
  ribbonModeButton: {
    marginTop: "0px",
    fill: customTokens.legacy_neutralDark,
  },
});

const useMenuIconStyles = makeStyles({
  medium: {
    fontSize: "12px",
    height: "12px",
    lineHeight: "12px",
    marginLeft: "4px",
    marginRight: "2px",
    width: "12px",
    fill: customTokens.legacy_neutralDark,
  },
  small: {
    height: "8px",
    fontSize: "8px",
    lineHeight: "8px",
    width: "8px",
    marginRight: "1px",
    marginLeft: "4px",
    fill: customTokens.legacy_neutralDark,
  },
  disabled: {
    opacity: ".4",
    [HighContrastSelector]: {
      opacity: "1",
    },
  },
});

export const useRibbonMenuButtonStyles = (
  state: RibbonMenuButtonState
): RibbonMenuButtonState => {
  const baseStyles = useBaseStyles();
  const iconStyles = useIconStyles();

  state.root.className = mergeClasses(
    getRibbonButtonTypeStyles(state as RibbonButtonState, baseStyles),
    !state.menuHidden && baseStyles.expanded,
    state.variant === "overflowButton" && baseStyles.overflowButton,
    state.root.className
  );

  if (state.icon) {
    state.icon.className = mergeClasses(
      getRibbonButtonTypeStyles(state as RibbonButtonState, iconStyles),
      state.icon.className
    );
  }

  const menuIconStyles = useMenuIconStyles();
  if (state.menuIcon) {
    state.menuIcon.className = mergeClasses(
      state.disabled && menuIconStyles.disabled,
      getRibbonButtonTypeStyles(state as RibbonButtonState, menuIconStyles),
      state.menuIcon.className
    );
  }

  // Fill in iconPosition because that is the only thing RibbonMenuButtonState doesn't have
  // that RibbonButtonState has
  useCommonRibbonButtonStyles({
    ...state,
    iconPosition: "before",
  } as RibbonButtonState);
  return state;
};
